import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import HierarchyPreview from './HierarchyPreview';
import CostSheetSelector from './CostSheetSelector';
import { useTranslation } from 'react-i18next';
import {
  createProjectTimeline,
  createTimelinePhases,
  createTimelinePhaseGroups,
  createTimelineFloors,
  updateTimelineFloor,
  createTimelineTask,
  updateTimelineTask,
  linkTimelineTaskCostSheets,
  unlinkTimelineTaskCostSheet,
  saveTimelineWizardProgress,
  updateProjectTimeline,
  getGeneralTasks,
  createGeneralTask
} from '../../services/api';
import { compareCatalogTasks } from '../../utils/generalTasks';

const DEPENDENCY_TYPES = [
  { value: 'START_START', label: 'Start-Start' },
  { value: 'START_FINISH', label: 'Start-Finish' },
  { value: 'FINISH_START', label: 'Finish-Start' },
  { value: 'FINISH_FINISH', label: 'Finish-Finish' }
];

const TimelineWizard = ({ open, onClose, projectId, projectName, existingTimeline = null }) => {
  const { t } = useTranslation();
  const steps = [
    t('timelineWizard.steps.numberOfPhases', { defaultValue: 'Number of Phases' }),
    t('timelineWizard.steps.phaseNamesGroups', { defaultValue: 'Phase Names & Groups' }),
    t('timelineWizard.steps.floorConfiguration', { defaultValue: 'Floor Configuration' }),
    t('timelineWizard.steps.floorDetails', { defaultValue: 'Floor Details' }),
    t('timelineWizard.steps.wallSpecifications', { defaultValue: 'Wall Specifications' }),
    t('timelineWizard.steps.taskCreation', { defaultValue: 'Task Creation' }),
    t('timelineWizard.steps.costSheetsDependencies', { defaultValue: 'Cost Sheets & Dependencies' })
  ];
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [timelineId, setTimelineId] = useState(null);
  const [generalTaskOptions, setGeneralTaskOptions] = useState([]);
  const generalTasksSearchTimeoutRef = useRef(null);
  
  // Wizard data state
  const [wizardData, setWizardData] = useState({
    numberOfPhases: 1,
    phases: [],
    floors: [],
    tasks: [],
    wallSpecifications: {
      count: 1,
      thicknesses: ['']
    }
  });

  const resetWizardState = () => {
    setActiveStep(0);
    setTimelineId(null);
    setWizardData({
      numberOfPhases: 1,
      phases: [],
      floors: [],
      tasks: [],
      wallSpecifications: {
        count: 1,
        thicknesses: ['']
      }
    });
  };

  useEffect(() => {
    if (open && projectId) {
      // Always reset when opening for a (possibly) different project
      resetWizardState();
      initializeWizard();
    }
  }, [open, projectId]);

  // Fetch general tasks (used for initial load and debounced search)
  const fetchGeneralTasks = useCallback((search = '') => {
    getGeneralTasks(search)
      .then((res) => {
        const options = (res.data || []).map((t) => ({
          id: t.id,
          name: t.name,
          category: t.category || '',
          sortOrder: t.sortOrder,
          isMaster: Boolean(t.isMaster || t.organisationId == null),
        }));
        options.sort(compareCatalogTasks);
        setGeneralTaskOptions(options);
      })
      .catch(() => setGeneralTaskOptions([]));
  }, []);

  // Debounced server-side search (350ms) so we don't query on every keystroke
  const debouncedGeneralTasksSearch = useCallback((inputValue) => {
    if (generalTasksSearchTimeoutRef.current) clearTimeout(generalTasksSearchTimeoutRef.current);
    generalTasksSearchTimeoutRef.current = setTimeout(() => {
      fetchGeneralTasks((inputValue || '').trim());
      generalTasksSearchTimeoutRef.current = null;
    }, 350);
  }, [fetchGeneralTasks]);

  // Load all general tasks when user reaches step 5 (Task Creation)
  useEffect(() => {
    if (open && activeStep === 5) {
      fetchGeneralTasks('');
    }
  }, [open, activeStep, fetchGeneralTasks]);


  const initializeWizard = async () => {
    try {
      setLoading(true);
      setActiveStep(0);
      
      if (existingTimeline) {
        // Load existing timeline data
        setTimelineId(existingTimeline.id);
        
        // Convert existing timeline data to wizard format expected by steps 3/4/6
        const phases = (existingTimeline.phases || []).map((phase) => ({
          id: phase.id,
          name: phase.name,
          numberOfGroups: phase.groups?.length || 1,
          groups: (phase.groups || []).map((group) => ({
            id: group.id,
            name: group.name,
          })),
        }));

        // Floors shape: floors[phaseIndex][groupIndex] must be an object with:
        // - numberOfFloors property
        // - numeric indices [0], [1], etc. for each floor data
        const floors = [];
        const tasks = [];

        (existingTimeline.phases || []).forEach((phase, pIndex) => {
          // Initialize arrays for this phase
          floors[pIndex] = [];
          tasks[pIndex] = [];

          (phase.groups || []).forEach((group, gIndex) => {
            // Get floors for this group from the API response
            const groupFloors = group.floors || [];
            const numFloors = groupFloors.length;
            
            // Create object that acts like both an object (with numberOfFloors) and array-like (with numeric indices)
            const floorsContainer = {
              numberOfFloors: numFloors || 1
            };

            // Initialize tasks array for this group
            tasks[pIndex][gIndex] = [];

            // Map each floor from the API response
            groupFloors.forEach((floor, fIndex) => {
              // Store floor data at numeric index (JavaScript will convert to string key)
              floorsContainer[fIndex] = {
                id: floor.id,
                name: String(floor.name || `Floor ${fIndex + 1}`),
                area: Number(floor.area) || 0,
                height: Number(floor.height) || 0,
              };

              // Load tasks for this floor
              const savedTasks = (floor.tasks || []).map((t) => ({
                id: t.id,
                name: t.name,
                hasWalls: !!t.hasWalls,
                hasDeck: !!t.hasDeck,
                wallLength: t.wallLength ?? 0,
                deckArea: t.deckArea ?? floor.area ?? 0,
                duration: t.duration ?? 1,
                dependencyType: t.dependencyType || '',
                dependencyTaskId: t.dependencyTaskId || null,
                selectedCostSheets: (t.costSheets || []).map((cs) => cs.costSheet).filter(Boolean),
              }));

              tasks[pIndex][gIndex][fIndex] = savedTasks;
            });
            
            // Assign the floors container to the correct position
            floors[pIndex][gIndex] = floorsContainer;
          });
        });

        const wizardDataToSet = {
          numberOfPhases: phases.length || 1,
          phases,
          floors,
          tasks,
          wallSpecifications: existingTimeline.wallThicknesses || {
            count: 1,
            thicknesses: [''],
          },
        };

        setWizardData(wizardDataToSet);
      } else {
        // Create new timeline with startDate set to today
        const today = new Date();
        today.setHours(8, 0, 0, 0); // Set to 8 AM
        const response = await createProjectTimeline({
          projectId,
          wallThicknesses: {
            count: 1,
            thicknesses: ['']
          },
          startDate: today.toISOString()
        });
        setTimelineId(response.data.id);
      }
    } catch (error) {
      console.error('Error initializing wizard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    try {
      setLoading(true);

      // When leaving step 5 (Task Creation), persist any custom task names to general tasks
      if (activeStep === 5) {
        const existingSet = new Set(
          generalTaskOptions.map((t) => (t.name || '').toLowerCase().trim()).filter(Boolean)
        );
        const customNames = new Set();
        (wizardData.tasks || []).forEach((phaseTasks) => {
          (phaseTasks || []).forEach((groupTasks) => {
            (groupTasks || []).forEach((floorTasks) => {
              (floorTasks || []).forEach((task) => {
                const name = (task.name || '').trim();
                if (name && !existingSet.has(name.toLowerCase())) {
                  customNames.add(name);
                }
              });
            });
          });
        });
        for (const name of customNames) {
          try {
            await createGeneralTask({ name });
            setGeneralTaskOptions((prev) => [
              ...prev,
              { id: null, name, category: '', isMaster: false },
            ]);
            existingSet.add(name.toLowerCase());
          } catch (e) {
            // ignore duplicate or server errors; list may already have it
          }
        }
      }
      
      // Save progress at checkpoints
      if (activeStep === 2 || activeStep === 4) {
        await saveWizardProgress();
      }
      
      setActiveStep((prevStep) => prevStep + 1);
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleFinish = async () => {
    try {
      setLoading(true);
      
      // Create all timeline structure
      await createTimelineStructure();
      
      onClose();
    } catch (error) {
      console.error('Error finishing wizard:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveWizardProgress = async () => {
    if (timelineId) {
      await saveTimelineWizardProgress(timelineId, activeStep, wizardData);
    }
  };

  const createTimelineStructure = async () => {
    try {
      // Create phases
      const phases = (wizardData.phases || []).map((phase, index) => ({
        name: phase.name,
        order: index + 1
      }));
      
      let createdPhases = existingTimeline?.phases || [];
      if (!existingTimeline && phases.length > 0) {
        createdPhases = await createTimelinePhases(timelineId, phases).then(r => r.data);
      }

      // Create groups, floors and tasks per phase
      for (let pIndex = 0; pIndex < (wizardData.phases || []).length; pIndex++) {
        const phaseInput = wizardData.phases[pIndex];
        const phaseId = createdPhases[pIndex]?.id;
        if (!phaseId) continue;

        // Build groups payload
        const groupsPayload = Array.from({ length: phaseInput.numberOfGroups || 1 }, (_, gIndex) => ({
          name: phaseInput.groups?.[gIndex]?.name || `Group ${gIndex + 1}`,
          order: gIndex + 1,
        }));

        let createdGroups = phaseInput.groups?.map((g) => ({ id: g.id, name: g.name })) || [];
        if (!existingTimeline) {
          createdGroups = await createTimelinePhaseGroups(phaseId, groupsPayload).then(r => r.data);
        }

        // Per group floors
        for (let gIndex = 0; gIndex < groupsPayload.length; gIndex++) {
          const groupId = createdGroups[gIndex]?.id;
          if (!groupId) continue;
          const floorsContainer = wizardData.floors?.[pIndex]?.[gIndex];
          const numFloors = (floorsContainer && typeof floorsContainer === 'object' && !Array.isArray(floorsContainer)
            ? floorsContainer.numberOfFloors
            : Array.isArray(floorsContainer) ? floorsContainer.length : 1) || 1;
          
          // Extract floor data - floorsContainer can be an object with numberOfFloors and numeric indices
          const floorsPayload = Array.from({ length: numFloors }, (_, fIndex) => {
            let f = {};
            if (Array.isArray(floorsContainer)) {
              f = floorsContainer[fIndex] || {};
            } else if (floorsContainer && typeof floorsContainer === 'object') {
              // Object with numeric indices (from Step 4) or just numberOfFloors (from Step 3)
              f = floorsContainer[fIndex] || {};
            }
            return {
              name: f.name || `Floor ${fIndex + 1}`,
              order: fIndex + 1,
              area: Number(f.area || 0),
              height: Number(f.height || 0),
            };
          });

          let createdFloors = [];
          if (existingTimeline) {
            // When editing, get floor IDs from existing timeline and update them
            const existingGroup = existingTimeline.phases?.[pIndex]?.groups?.[gIndex];
            const existingFloors = existingGroup?.floors || [];
            
            // Update each existing floor with new details from wizardData
            for (let fIndex = 0; fIndex < existingFloors.length; fIndex++) {
              const existingFloor = existingFloors[fIndex];
              const floorData = floorsPayload[fIndex];
              
              if (existingFloor && floorData) {
                await updateTimelineFloor(existingFloor.id, {
                  name: floorData.name,
                  order: floorData.order,
                  area: floorData.area,
                  height: floorData.height
                });
              }
            }
            
            createdFloors = existingFloors.map((f) => ({ id: f.id, name: f.name }));
          } else {
            // When creating new, create floors and get their IDs
            if (floorsPayload.length > 0) {
              createdFloors = await createTimelineFloors(groupId, floorsPayload).then(r => r.data);
            }
          }

          // Per floor tasks
          for (let fIndex = 0; fIndex < createdFloors.length; fIndex++) {
            const floorId = createdFloors[fIndex]?.id;
            if (!floorId) continue;
            const tasksForFloorContainer = wizardData.tasks?.[pIndex]?.[gIndex]?.[fIndex];
            const tasksForFloor = Array.isArray(tasksForFloorContainer) ? tasksForFloorContainer : [];
            for (let tIndex = 0; tIndex < tasksForFloor.length; tIndex++) {
              const t = tasksForFloor[tIndex];
              // Only create if it has a name
              if (!t?.name) continue;
              
              // Create or update task
              let taskId;
              if (existingTimeline && t.id) {
                // Update existing task
                const updatedTask = await updateTimelineTask(t.id, {
                  name: t.name,
                  hasWalls: !!t.hasWalls,
                  hasDeck: !!t.hasDeck,
                  wallLength: t.hasWalls ? Number(t.wallLength || 0) : null,
                  deckArea: t.hasDeck ? Number(t.deckArea || 0) : null,
                  duration: Number(t.duration || 1),
                  dependencyType: t.dependencyType || null,
                  dependencyTaskId: t.dependencyTaskId || null,
                  startDate: t.startDate || null,
                  endDate: t.endDate || null,
                });
                taskId = updatedTask.data.id;
              } else {
                // Create new task
                const createdTask = await createTimelineTask(floorId, {
                  name: t.name,
                  hasWalls: !!t.hasWalls,
                  hasDeck: !!t.hasDeck,
                  wallLength: t.hasWalls ? Number(t.wallLength || 0) : null,
                  deckArea: t.hasDeck ? Number(t.deckArea || 0) : null,
                  duration: Number(t.duration || 1),
                  dependencyType: t.dependencyType || null,
                  dependencyTaskId: t.dependencyTaskId || null,
                  startDate: t.startDate || null,
                  endDate: t.endDate || null,
                });
                taskId = createdTask.data.id;
              }
              
              // Link cost sheets to task
              if (taskId) {
                const costSheetIds = (t.selectedCostSheets || [])
                  .map(cs => parseInt(cs.id || cs))
                  .filter(id => !isNaN(id));
                
                try {
                  // For existing tasks, we need to replace cost sheet links
                  // First, get existing cost sheets to see what needs to be removed
                  if (existingTimeline && t.id) {
                    // Get current cost sheet links from the existing task
                    const existingTask = existingTimeline.phases?.[pIndex]?.groups?.[gIndex]?.floors?.[fIndex]?.tasks?.find(tt => tt.id === t.id);
                    const existingCostSheetIds = (existingTask?.costSheets || [])
                      .map(cs => parseInt(cs.costSheetId || cs.costSheet?.id))
                      .filter(id => !isNaN(id));
                    
                    // Find cost sheets to remove (in existing but not in new selection)
                    const toRemove = existingCostSheetIds.filter(id => !costSheetIds.includes(id));
                    // Find cost sheets to add (in new selection but not in existing)
                    const toAdd = costSheetIds.filter(id => !existingCostSheetIds.includes(id));
                    
                    // Remove old links
                    for (const costSheetId of toRemove) {
                      try {
                        await unlinkTimelineTaskCostSheet(taskId, costSheetId);
                      } catch (err) {
                        console.error(`Error unlinking cost sheet ${costSheetId} from task ${taskId}:`, err);
                      }
                    }
                    
                    // Add new links
                    if (toAdd.length > 0) {
                      await linkTimelineTaskCostSheets(taskId, toAdd);
                    }
                  } else {
                    // For new tasks, just link all selected cost sheets
                    if (costSheetIds.length > 0) {
                      await linkTimelineTaskCostSheets(taskId, costSheetIds);
                    }
                  }
                } catch (error) {
                  console.error(`Error managing cost sheets for task ${taskId}:`, error);
                  // Don't throw - continue with other tasks
                }
              }
            }
          }
        }
      }

      // Persist global wall specifications and startDate
      if (timelineId && wizardData.wallSpecifications) {
        const updateData = {
          wallThicknesses: wizardData.wallSpecifications
        };
        // If this is a new timeline and we don't have a startDate yet, set it to today
        if (!existingTimeline && !wizardData.startDate) {
          const today = new Date();
          today.setHours(8, 0, 0, 0); // Set to 8 AM
          updateData.startDate = today.toISOString();
        }
        await updateProjectTimeline(timelineId, updateData);
      }
      
      await saveWizardProgress();
    } catch (error) {
      console.error('Error creating timeline structure:', error);
      throw error;
    }
  };

  const updateWizardData = (updates) => {
    setWizardData(prev => ({ ...prev, ...updates }));
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              How many phases does this project have?
            </Typography>
            <TextField
              fullWidth
              type="number"
              label="Number of Phases"
              value={wizardData.numberOfPhases}
              onChange={(e) => {
                const value = e.target.value;
                updateWizardData({ numberOfPhases: value === '' ? '' : Math.max(1, parseInt(value, 10) || 1) });
              }}
              inputProps={{ min: 1, max: 10, step: 1 }}
              helperText="Enter the number of main phases for your project (e.g., Foundation, Structure, Finishing)"
            />
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Define Phase Names and Work Groups
            </Typography>
            {Array.from({ length: wizardData.numberOfPhases }, (_, index) => (
              <Card key={index} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Phase {index + 1}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Phase Name"
                        value={wizardData.phases[index]?.name || ''}
                        onChange={(e) => {
                          const updatedPhases = [...wizardData.phases];
                          updatedPhases[index] = { ...updatedPhases[index], name: e.target.value };
                          updateWizardData({ phases: updatedPhases });
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Number of Work Groups"
                        value={wizardData.phases[index]?.numberOfGroups ?? 1}
                        onChange={(e) => {
                          const count = e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1);
                          const updatedPhases = [...wizardData.phases];
                          const existingGroups = updatedPhases[index]?.groups || [];
                          if (count === '') {
                            updatedPhases[index] = {
                              ...updatedPhases[index],
                              numberOfGroups: '',
                              groups: existingGroups,
                            };
                            updateWizardData({ phases: updatedPhases });
                            return;
                          }
                          // Resize groups array to the new count, preserving names
                          const newGroups = Array.from({ length: count }, (_, gIndex) => ({
                            name: existingGroups[gIndex]?.name || `Group ${gIndex + 1}`,
                          }));
                          updatedPhases[index] = { 
                            ...updatedPhases[index], 
                            numberOfGroups: count,
                            groups: newGroups,
                          };
                          updateWizardData({ phases: updatedPhases });
                        }}
                        inputProps={{ min: 1, max: 10, step: 1 }}
                      />
                    </Grid>
                  </Grid>
                  {/* Group naming inputs */}
                  <Box sx={{ mt: 2 }}>
                    {Array.from({ length: wizardData.phases[index]?.numberOfGroups || 1 }, (_, gIndex) => (
                      <Grid container spacing={2} key={gIndex} sx={{ mb: 1 }}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label={`Work Group ${gIndex + 1} Name`}
                            value={wizardData.phases[index]?.groups?.[gIndex]?.name || `Group ${gIndex + 1}`}
                            onChange={(e) => {
                              const updatedPhases = [...wizardData.phases];
                              const phase = updatedPhases[index] || {};
                              const groups = [...(phase.groups || Array.from({ length: wizardData.phases[index]?.numberOfGroups || 1 }, (_, i) => ({ name: `Group ${i + 1}` })))];
                              groups[gIndex] = { ...groups[gIndex], name: e.target.value };
                              updatedPhases[index] = { ...phase, groups };
                              updateWizardData({ phases: updatedPhases });
                            }}
                          />
                        </Grid>
                      </Grid>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Configure Floors for Each Work Group
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Note: The floor slab of the lowest floor is its own floor, and the attic walls on the roof are its own floor.
            </Alert>
            {wizardData.phases.map((phase, phaseIndex) => (
              <Card key={phaseIndex} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {phase.name}
                  </Typography>
                  {Array.from({ length: phase.numberOfGroups }, (_, groupIndex) => (
                    <Box key={groupIndex} sx={{ mb: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        {phase.groups?.[groupIndex]?.name || `Work Group ${groupIndex + 1}`}
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        label="Number of Floors"
                        value={wizardData.floors?.[phaseIndex]?.[groupIndex]?.numberOfFloors ?? 1}
                        onChange={(e) => {
                          const updatedFloors = [...wizardData.floors];
                          if (!updatedFloors[phaseIndex]) updatedFloors[phaseIndex] = [];
                          const value = e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1);
                          updatedFloors[phaseIndex][groupIndex] = { 
                            ...updatedFloors[phaseIndex][groupIndex], 
                            numberOfFloors: value,
                          };
                          updateWizardData({ floors: updatedFloors });
                        }}
                        inputProps={{ min: 1, max: 20, step: 1 }}
                      />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            ))}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Floor Details
            </Typography>
            {wizardData.phases.map((phase, phaseIndex) => (
              <Card key={phaseIndex} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {phase.name}
                  </Typography>
                  {Array.from({ length: phase.numberOfGroups }, (_, groupIndex) => (
                    <Box key={groupIndex} sx={{ mb: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        {phase.groups?.[groupIndex]?.name || `Work Group ${groupIndex + 1}`}
                      </Typography>
                      {Array.from({ length: wizardData.floors[phaseIndex]?.[groupIndex]?.numberOfFloors || 1 }, (_, floorIndex) => (
                        <Card key={floorIndex} sx={{ mb: 1, p: 2 }}>
                          <Typography variant="body2" gutterBottom>
                            Floor {floorIndex + 1}
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label="Floor Name/Number"
                                value={wizardData.floors[phaseIndex]?.[groupIndex]?.[floorIndex]?.name || `Floor ${floorIndex + 1}`}
                                onChange={(e) => {
                                  const updatedFloors = [...wizardData.floors];
                                  if (!updatedFloors[phaseIndex]) updatedFloors[phaseIndex] = [];
                                  if (!updatedFloors[phaseIndex][groupIndex]) updatedFloors[phaseIndex][groupIndex] = [];
                                  updatedFloors[phaseIndex][groupIndex][floorIndex] = { 
                                    ...updatedFloors[phaseIndex][groupIndex][floorIndex], 
                                    name: e.target.value 
                                  };
                                  updateWizardData({ floors: updatedFloors });
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                type="number"
                                label="Area (m²)"
                                value={wizardData.floors[phaseIndex]?.[groupIndex]?.[floorIndex]?.area || ''}
                                onChange={(e) => {
                                  const updatedFloors = [...wizardData.floors];
                                  if (!updatedFloors[phaseIndex]) updatedFloors[phaseIndex] = [];
                                  if (!updatedFloors[phaseIndex][groupIndex]) updatedFloors[phaseIndex][groupIndex] = [];
                                  updatedFloors[phaseIndex][groupIndex][floorIndex] = { 
                                    ...updatedFloors[phaseIndex][groupIndex][floorIndex], 
                                    area: parseFloat(e.target.value) || 0 
                                  };
                                  updateWizardData({ floors: updatedFloors });
                                }}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                type="number"
                                label="Height (m)"
                                value={wizardData.floors[phaseIndex]?.[groupIndex]?.[floorIndex]?.height || ''}
                                onChange={(e) => {
                                  const updatedFloors = [...wizardData.floors];
                                  if (!updatedFloors[phaseIndex]) updatedFloors[phaseIndex] = [];
                                  if (!updatedFloors[phaseIndex][groupIndex]) updatedFloors[phaseIndex][groupIndex] = [];
                                  updatedFloors[phaseIndex][groupIndex][floorIndex] = { 
                                    ...updatedFloors[phaseIndex][groupIndex][floorIndex], 
                                    height: parseFloat(e.target.value) || 0 
                                  };
                                  updateWizardData({ floors: updatedFloors });
                                }}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            ))}
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Wall Specifications
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Define the wall thicknesses (in meters) that will be used across the whole project.
            </Typography>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Number of Different Wall Thicknesses"
                      value={wizardData.wallSpecifications?.count ?? 1}
                      onChange={(e) => {
                        const count = e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1);
                        if (count === '') {
                          updateWizardData({
                            wallSpecifications: {
                              count: '',
                              thicknesses: wizardData.wallSpecifications?.thicknesses || [''],
                            },
                          });
                          return;
                        }
                        const current = wizardData.wallSpecifications?.thicknesses || [];
                        const updatedThicknesses = Array.from({ length: count }, (_, idx) => current[idx] ?? '');
                        updateWizardData({
                          wallSpecifications: {
                            count,
                            thicknesses: updatedThicknesses
                          }
                        });
                      }}
                      inputProps={{ min: 1, max: 10, step: 1 }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3 }}>
                  {Array.from({ length: wizardData.wallSpecifications?.count ?? 1 }, (_, idx) => (
                    <TextField
                      key={idx}
                      fullWidth
                      type="number"
                      label={`Wall ${idx + 1} Thickness (m)`}
                      value={wizardData.wallSpecifications?.thicknesses?.[idx] ?? ''}
                      onChange={(e) => {
                        const specs = wizardData.wallSpecifications || { count: 1, thicknesses: [''] };
                        const updated = [...(specs.thicknesses || [])];
                        updated[idx] = e.target.value;
                        updateWizardData({
                          wallSpecifications: {
                            count: specs.count,
                            thicknesses: updated
                          }
                        });
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={{ mb: 2 }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>
        );

      case 5:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Create Tasks for Each Floor
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              For each floor, create tasks that need to be completed. Specify if the task involves walls or deck work.
            </Typography>
            {wizardData.phases.map((phase, phaseIndex) => (
              <Card key={phaseIndex} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {phase.name}
                  </Typography>
                  {Array.from({ length: phase.numberOfGroups }, (_, groupIndex) => (
                    <Box key={groupIndex} sx={{ mb: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        {phase.groups?.[groupIndex]?.name || `Work Group ${groupIndex + 1}`}
                      </Typography>
                      {Array.from({ length: wizardData.floors[phaseIndex]?.[groupIndex]?.numberOfFloors || 1 }, (_, floorIndex) => (
                        <Card key={floorIndex} sx={{ mb: 1, p: 2 }}>
                          <Typography variant="body2" gutterBottom>
                            {wizardData.floors[phaseIndex]?.[groupIndex]?.[floorIndex]?.name || `Floor ${floorIndex + 1}`}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <Button
                              startIcon={<AddIcon />}
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                const updatedTasks = [...wizardData.tasks];
                                if (!updatedTasks[phaseIndex]) updatedTasks[phaseIndex] = [];
                                if (!updatedTasks[phaseIndex][groupIndex]) updatedTasks[phaseIndex][groupIndex] = [];
                                if (!updatedTasks[phaseIndex][groupIndex][floorIndex]) updatedTasks[phaseIndex][groupIndex][floorIndex] = [];
                                updatedTasks[phaseIndex][groupIndex][floorIndex].push({
                                  name: '',
                                  hasWalls: false,
                                  hasDeck: false,
                                  wallLength: 0,
                                  deckArea: wizardData.floors[phaseIndex]?.[groupIndex]?.[floorIndex]?.area || 0
                                });
                                updateWizardData({ tasks: updatedTasks });
                              }}
                            >
                              Add Task
                            </Button>
                          </Box>
                          {wizardData.tasks[phaseIndex]?.[groupIndex]?.[floorIndex]?.map((task, taskIndex) => (
                            <Card key={taskIndex} sx={{ mb: 1, p: 2, border: '1px solid #e0e0e0' }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Autocomplete
                                    freeSolo
                                    options={generalTaskOptions}
                                    groupBy={(option) => {
                                      if (typeof option === 'string') return '';
                                      const category = option.category || '';
                                      return option.isMaster
                                        ? `${category || 'System'} · System`
                                        : category || 'Organisation';
                                    }}
                                    filterOptions={(options, state) => {
                                      const input = (state.inputValue || '').trim().toLowerCase();
                                      if (!input) return options;
                                      return options.filter((opt) => {
                                        const name = typeof opt === 'string' ? opt : opt?.name || '';
                                        const category = typeof opt === 'string' ? '' : opt?.category || '';
                                        return (
                                          name.toLowerCase().includes(input) ||
                                          category.toLowerCase().includes(input)
                                        );
                                      });
                                    }}
                                    value={task.name || ''}
                                    onInputChange={(_, inputValue) => {
                                      const updatedTasks = [...wizardData.tasks];
                                      updatedTasks[phaseIndex][groupIndex][floorIndex][taskIndex].name = inputValue || '';
                                      updateWizardData({ tasks: updatedTasks });
                                      debouncedGeneralTasksSearch(inputValue);
                                    }}
                                    onChange={(_, value) => {
                                      const updatedTasks = [...wizardData.tasks];
                                      const name =
                                        typeof value === 'string'
                                          ? value
                                          : value?.name || '';
                                      updatedTasks[phaseIndex][groupIndex][floorIndex][taskIndex].name = name;
                                      updateWizardData({ tasks: updatedTasks });
                                    }}
                                    getOptionLabel={(option) =>
                                      typeof option === 'string' ? option : option?.name || ''
                                    }
                                    isOptionEqualToValue={(option, value) => {
                                      const optName = typeof option === 'string' ? option : option?.name || '';
                                      const valName = typeof value === 'string' ? value : value?.name || '';
                                      return optName === valName;
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Task Name"
                                        placeholder="Choose or type a task name"
                                      />
                                    )}
                                  />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={task.hasWalls}
                                          onChange={(e) => {
                                            const updatedTasks = [...wizardData.tasks];
                                            updatedTasks[phaseIndex][groupIndex][floorIndex][taskIndex].hasWalls = e.target.checked;
                                            updateWizardData({ tasks: updatedTasks });
                                          }}
                                        />
                                      }
                                      label="Has Walls"
                                    />
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={task.hasDeck}
                                          onChange={(e) => {
                                            const updatedTasks = [...wizardData.tasks];
                                            updatedTasks[phaseIndex][groupIndex][floorIndex][taskIndex].hasDeck = e.target.checked;
                                            updateWizardData({ tasks: updatedTasks });
                                          }}
                                        />
                                      }
                                      label="Has Deck"
                                    />
                                  </Box>
                                </Grid>
                                {task.hasWalls && (
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      fullWidth
                                      type="number"
                                      label="Wall Length (m)"
                                      value={task.wallLength}
                                      onChange={(e) => {
                                        const updatedTasks = [...wizardData.tasks];
                                        updatedTasks[phaseIndex][groupIndex][floorIndex][taskIndex].wallLength = parseFloat(e.target.value) || 0;
                                        updateWizardData({ tasks: updatedTasks });
                                      }}
                                      inputProps={{ min: 0, step: 0.01 }}
                                    />
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="caption" color="text.secondary">
                                        Available thicknesses: {(wizardData.wallSpecifications?.thicknesses || [])
                                          .filter(Boolean)
                                          .join(', ') || 'Not defined'}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                )}
                                {task.hasDeck && (
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      fullWidth
                                      type="number"
                                      label="Deck Area (m²)"
                                      value={task.deckArea}
                                      onChange={(e) => {
                                        const updatedTasks = [...wizardData.tasks];
                                        updatedTasks[phaseIndex][groupIndex][floorIndex][taskIndex].deckArea = parseFloat(e.target.value) || 0;
                                        updateWizardData({ tasks: updatedTasks });
                                      }}
                                      inputProps={{ min: 0, step: 0.01 }}
                                    />
                                  </Grid>
                                )}
                                <Grid item xs={12}>
                                  <IconButton
                                    color="error"
                                    onClick={() => {
                                      const updatedTasks = [...wizardData.tasks];
                                      updatedTasks[phaseIndex][groupIndex][floorIndex].splice(taskIndex, 1);
                                      updateWizardData({ tasks: updatedTasks });
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            </Card>
                          ))}
                        </Card>
                      ))}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            ))}
          </Box>
        );

      case 6:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Connect Cost Sheets and Set Dependencies
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              For each task, connect relevant cost sheets and set up task dependencies.
            </Typography>
            {wizardData.phases.map((phase, phaseIndex) => (
              <Card key={phaseIndex} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {phase.name}
                  </Typography>
                  {Array.from({ length: phase.numberOfGroups }, (_, groupIndex) => (
                    <Box key={groupIndex} sx={{ mb: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        {phase.groups?.[groupIndex]?.name || `Work Group ${groupIndex + 1}`}
                      </Typography>
                      {Array.from({ length: wizardData.floors[phaseIndex]?.[groupIndex]?.numberOfFloors || 1 }, (_, floorIndex) => (
                        <Card key={floorIndex} sx={{ mb: 1, p: 2 }}>
                          <Typography variant="body2" gutterBottom>
                            {wizardData.floors[phaseIndex]?.[groupIndex]?.[floorIndex]?.name || `Floor ${floorIndex + 1}`}
                          </Typography>
                          {wizardData.tasks[phaseIndex]?.[groupIndex]?.[floorIndex]?.map((task, taskIndex) => (
                            <Card key={taskIndex} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0' }}>
                              <Typography variant="body2" gutterBottom>
                                Task: {task.name}
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    fullWidth
                                    type="number"
                                    label="Duration (days)"
                                    value={task.duration || ''}
                                    onChange={(e) => {
                                      const updatedTasks = [...wizardData.tasks];
                                      updatedTasks[phaseIndex][groupIndex][floorIndex][taskIndex].duration =
                                        e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1);
                                      updateWizardData({ tasks: updatedTasks });
                                    }}
                                    inputProps={{ min: 1, step: 1 }}
                                  />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <FormControl fullWidth>
                                    <InputLabel>Dependency Type</InputLabel>
                                    <Select
                                      value={task.dependencyType || ''}
                                      onChange={(e) => {
                                        const updatedTasks = [...wizardData.tasks];
                                        updatedTasks[phaseIndex][groupIndex][floorIndex][taskIndex].dependencyType = e.target.value;
                                        updateWizardData({ tasks: updatedTasks });
                                      }}
                                    >
                                      <MenuItem value="">{t('timelineWizard.noDependency', { defaultValue: 'No Dependency' })}</MenuItem>
                                      {DEPENDENCY_TYPES.map((type) => (
                                        <MenuItem key={type.value} value={type.value}>
                                          {type.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                  <CostSheetSelector
                                    projectId={projectId}
                                    selectedCostSheets={task.selectedCostSheets || []}
                                    onCostSheetsChange={(costSheets) => {
                                      const updatedTasks = [...wizardData.tasks];
                                      updatedTasks[phaseIndex][groupIndex][floorIndex][taskIndex].selectedCostSheets = costSheets;
                                      updateWizardData({ tasks: updatedTasks });
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </Card>
                          ))}
                        </Card>
                      ))}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            ))}
          </Box>
        );

      default:
        return t('timelineWizard.unknownStep', { defaultValue: 'Unknown step' });
    }
  };

  // Compute normalized structure for preview from wizardData
  const normalizedForPreview = {
    phases: (wizardData.phases || []).map((p, pIndex) => ({
      id: p.id || pIndex,
      name: p.name || `Phase ${pIndex + 1}`,
      groups: Array.from({ length: (p.numberOfGroups ?? (p.groups?.length || 1)) }, (_, gIndex) => {
        const g = p.groups?.[gIndex] || {};
        const floorsMeta = wizardData.floors?.[pIndex]?.[gIndex];
        const numFloors = (floorsMeta?.numberOfFloors ?? (Array.isArray(floorsMeta) ? floorsMeta.length : 1));
        return {
          id: g.id || `${pIndex}-${gIndex}`,
          name: g.name || `Group ${gIndex + 1}`,
          floors: Array.from({ length: numFloors }, (_, fIndex) => {
            const f = Array.isArray(floorsMeta) ? (floorsMeta[fIndex] || {}) : {};
            const tasks = wizardData.tasks?.[pIndex]?.[gIndex]?.[fIndex] || [];
            return {
              id: f.id || `${pIndex}-${gIndex}-${fIndex}`,
              name: f.name || `Floor ${fIndex + 1}`,
              area: f.area || 0,
              height: f.height || 0,
              tasks: tasks,
            };
          }),
        };
      }),
    })),
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {existingTimeline ? t('projectTimelines.editTimeline') : t('projectTimelines.createTimeline')} - {projectName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              {t('common.back', { defaultValue: 'Back' })}
            </Button>
            <Button
              endIcon={<ArrowForwardIcon />}
              onClick={activeStep === steps.length - 1 ? handleFinish : handleNext}
              variant="contained"
              disabled={loading}
            >
              {activeStep === steps.length - 1 ? t('common.finish', { defaultValue: 'Finish' }) : t('common.next', { defaultValue: 'Next' })}
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                {renderStepContent()}
              </Grid>
              <Grid item xs={12} md={5}>
                <HierarchyPreview timelineData={normalizedForPreview} />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimelineWizard;
